import { useMemo, useState, type ReactElement } from 'react';
import { FileSignature, Plus, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/shell/PageHeader';

import {
  useAdminConsentTypeMutations,
  useAdminConsentTypes,
  type AdminConsentTypeInput,
  type AdminConsentTypeViewModel,
  type UpdateAdminConsentTypeInput,
} from '@/features/admin-consent-types/api';
import { AdminConsentTypesTable } from '@/features/admin-consent-types/components/AdminConsentTypesTable';
import { ConsentTypeDialog } from '@/features/admin-consent-types/components/ConsentTypeDialog';

export function AdminConsentTypesPage(): ReactElement {
  const { t } = useTranslation();
  const query = useAdminConsentTypes('all');
  const { create, update } = useAdminConsentTypeMutations();

  const [createOpen, setCreateOpen] = useState(false);
  const [pendingEdit, setPendingEdit] = useState<AdminConsentTypeViewModel | null>(null);
  const [search, setSearch] = useState('');

  // Sort by admin-configured sortOrder, name as tiebreaker so ties render stably.
  const sortedRows = useMemo(
    () =>
      query.isSuccess
        ? [...query.data].sort(
            (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
          )
        : [],
    [query.isSuccess, query.data],
  );

  // Client-side filter — the catalogue is small enough that no round-trip is
  // needed. Reorder arrows stay based on the *sorted* list's indices; when a
  // search is active they're hidden from the filtered view so admins don't
  // accidentally swap non-adjacent rows.
  const trimmedSearch = search.trim().toLowerCase();
  const isSearching = trimmedSearch.length > 0;
  const rows = useMemo(
    () =>
      isSearching
        ? sortedRows.filter(
            (r) =>
              r.name.toLowerCase().includes(trimmedSearch) ||
              r.description.toLowerCase().includes(trimmedSearch),
          )
        : sortedRows,
    [sortedRows, isSearching, trimmedSearch],
  );

  function handleCreate(input: AdminConsentTypeInput): void {
    // Append to the bottom of the list — the sortOrder field is no longer
    // manually entered; arrow reordering handles positioning after create.
    // Use `sortedRows` (unfiltered) so an active search doesn't skew max.
    const maxOrder =
      sortedRows.length === 0 ? 0 : Math.max(...sortedRows.map((r) => r.sortOrder));
    create.mutate(
      { ...input, sortOrder: maxOrder + 1 },
      {
        onSuccess: () => {
          setCreateOpen(false);
          create.reset();
        },
      },
    );
  }

  function handleUpdate(input: UpdateAdminConsentTypeInput): void {
    update.mutate(input, {
      onSuccess: () => {
        setPendingEdit(null);
        update.reset();
      },
    });
  }

  function handleToggleField(
    row: AdminConsentTypeViewModel,
    field: 'isRequired' | 'isActive',
  ): void {
    update.mutate({
      id: row.id,
      name: row.name,
      description: row.description,
      sortOrder: row.sortOrder,
      isRequired: field === 'isRequired' ? !row.isRequired : row.isRequired,
      isActive: field === 'isActive' ? !row.isActive : row.isActive,
      changed: [field],
    });
  }

  // Swap sortOrder between the row at `i` and its neighbor at `j` by
  // assigning each the *other's* display index. Uses `sortedRows`
  // (unfiltered) so an active search — which we disable reordering
  // under — can't produce non-adjacent swaps.
  function swapWithNeighbor(i: number, j: number): void {
    const row = sortedRows[i];
    const neighbor = sortedRows[j];
    if (row === undefined || neighbor === undefined) return;
    update.mutate({
      id: row.id,
      name: row.name,
      description: row.description,
      sortOrder: j,
      isRequired: row.isRequired,
      isActive: row.isActive,
      changed: ['sortOrder'],
    });
    update.mutate({
      id: neighbor.id,
      name: neighbor.name,
      description: neighbor.description,
      sortOrder: i,
      isRequired: neighbor.isRequired,
      isActive: neighbor.isActive,
      changed: ['sortOrder'],
    });
  }

  return (
    <>
      <PageHeader
        icon={<FileSignature className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={t('routes.admin.consentTypes.heading')}
        subtitle={t('routes.admin.consentTypes.subheading')}
      />
      <div className="px-8 py-5 space-y-8 bg-background">
        <section aria-labelledby="admin-consent-types-heading" className="space-y-4">
          <header className="flex items-start justify-between gap-4">
            <div>
              <h2
                id="admin-consent-types-heading"
                className="text-base font-semibold text-foreground"
              >
                {t('routes.admin.consentTypes.tableHeading')}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('routes.admin.consentTypes.tableSubheading')}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => {
                create.reset();
                setCreateOpen(true);
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t('routes.admin.consentTypes.addAction')}
            </Button>
          </header>

          {query.isSuccess && sortedRows.length > 0 && (
            <div className="relative max-w-md">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                placeholder={t('routes.admin.consentTypes.searchPlaceholder')}
                aria-label={t('routes.admin.consentTypes.searchAriaLabel')}
                className="pl-9 pr-9"
                autoComplete="off"
                spellCheck={false}
              />
              {search.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch('');
                  }}
                  aria-label={t('routes.admin.consentTypes.clearSearch')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              )}
            </div>
          )}

          {query.isPending && <RowsSkeleton />}

          {query.isError && (
            <div className="max-w-xl">
              <FetchErrorCallout
                error={query.error}
                heading={t('routes.admin.consentTypes.error.heading')}
                message={t('routes.admin.consentTypes.error.message')}
                codeLabel={t('routes.admin.consentTypes.error.codeLabel')}
                onRetry={() => {
                  query.refetch().catch(() => undefined);
                }}
                retryLabel={t('routes.admin.consentTypes.error.retry')}
              />
            </div>
          )}

          {query.isSuccess && sortedRows.length === 0 && (
            <p className="text-sm text-muted-foreground" role="status">
              {t('routes.admin.consentTypes.empty')}
            </p>
          )}

          {query.isSuccess && sortedRows.length > 0 && rows.length === 0 && (
            <p className="text-sm text-muted-foreground" role="status">
              {t('routes.admin.consentTypes.searchEmpty', { query: search.trim() })}
            </p>
          )}

          {query.isSuccess && rows.length > 0 && (
            <DataTable aria-label={t('routes.admin.consentTypes.tableHeading')}>
              <AdminConsentTypesTable
                rows={rows}
                reorderEnabled={!isSearching}
                onEdit={(row) => {
                  update.reset();
                  setPendingEdit(row);
                }}
                onMoveUp={(row) => {
                  const i = sortedRows.findIndex((r) => r.id === row.id);
                  if (i > 0) swapWithNeighbor(i, i - 1);
                }}
                onMoveDown={(row) => {
                  const i = sortedRows.findIndex((r) => r.id === row.id);
                  if (i >= 0 && i < sortedRows.length - 1) swapWithNeighbor(i, i + 1);
                }}
                onToggleRequired={(row) => {
                  handleToggleField(row, 'isRequired');
                }}
                onToggleActive={(row) => {
                  handleToggleField(row, 'isActive');
                }}
                pendingUpdateId={update.isPending ? update.variables.id : null}
              />
            </DataTable>
          )}
        </section>
      </div>

      <ConsentTypeDialog
        open={createOpen}
        row={null}
        onCancel={() => {
          create.reset();
          setCreateOpen(false);
        }}
        onCreate={handleCreate}
        onUpdate={() => undefined}
        pending={create.isPending}
        submitError={create.error}
      />

      <ConsentTypeDialog
        open={pendingEdit !== null}
        row={pendingEdit}
        onCancel={() => {
          update.reset();
          setPendingEdit(null);
        }}
        onCreate={() => undefined}
        onUpdate={handleUpdate}
        pending={update.isPending}
        submitError={update.error}
      />
    </>
  );
}

function RowsSkeleton(): ReactElement {
  return (
    <div className="space-y-2" aria-hidden="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full rounded-md" />
      ))}
    </div>
  );
}
